import type { LDContext, LDContextCommon } from "@launchdarkly/node-server-sdk";

// Feature flag keys from LaunchDarkly. Must match configured flags.

export enum Flag {
  OPEN_ENROLLMENT_CHAT_OPTION_ENABLED = "open-enrollment-chat-option",
  CASE_ONBOARDING_AGENT_HUB_PROMPT = "case-onboarding-agent-hub-prompt",
  ENDPOINT_CDP_PROVIDER_OVERRIDE = "endpoint-cdp-provider-override",
  GENERATE_REVIEW_FINDING_TASKS = "generate-review-finding-tasks",
  PLAYWRIGHT_CAPTURE_HAR_ARTIFACT = "playwright-capture-har-artifact",
  PLAYWRIGHT_FILL_STRATEGY = "playwright-fill-strategy",
  PLAYWRIGHT_MOVE_CURSOR_STRATEGY = "playwright-move-cursor-strategy",
  BROWSERBASE_PROXY_STRATEGY = "browserbase-proxy-strategy",
  BROWSERBASE_STEALTH_STRATEGY = "browserbase-stealth-strategy",
  BROWSERBASE_CUSTOM_SESSION_SETTINGS = "browserbase-custom-session-settings",
  RUN_CLASSIC_CRAWL = "run-classic-crawl",
  CLASSIC_CRAWL_REQUESTS_PER_SECOND = "classic-crawl-requests-per-second",
  RUN_AI_CRAWL = "run-ai-crawl",
  COMPARE_FAIR_MARKET_VALUE = "compare-fair-market-value",
  AI_CRAWL_ENABLE_CLAIMS_LIST_DISCOVERY = "ai-crawl-enable-claims-list-discovery",
  AI_CRAWL_ENABLE_CLAIM_DETAILS_VISITS = "ai-crawl-enable-claim-details-visits",
  AI_CRAWL_ENABLE_PLAN_DOCUMENTS = "ai-crawl-enable-plan-documents",
  AI_CRAWL_ENABLE_MEMBER_ID_CARDS = "ai-crawl-enable-member-id-cards",
  AI_CRAWL_ENABLE_MFA_CONFIGURATION_DISCOVERY = "ai-crawl-enable-mfa-configuration-discovery",
  AI_CRAWL_ENABLE_ACCUMULATORS_DISCOVERY = "ai-crawl-enable-accumulators-discovery",
  AI_CRAWL_ENABLE_PARALLELIZATION = "enable-ai-crawl-parallelization",
  INGESTION_V2 = "ingestion-v2",
  AI_CRAWL_FILE_GRABBER_MODEL = "ai-crawl-file-grabber-model",
  AI_CRAWL_STEPPER_V2_MODEL = "ai-crawl-stepper-v2-model",
  AI_CRAWL_LARGE_MODEL = "ai-crawl-large-model",
  AI_CRAWL_SMALL_MODEL = "ai-crawl-small-model",
  RPA_TEMPORAL_HEARTBEAT_SCHEDULE_SECONDS = "rpa-temporal-heartbeat-schedule-seconds",
  SIGN_IN_ROBOT_AI_MODEL = "sign-in-robot-ai-model",
  SIGN_IN_LOGIN_SENTINEL_MODEL = "sign-in-login-sentinel-model",
  PARSE_COMPLETER_MODEL = "parse-completer-model",
  SIGN_IN_DIRECTLY = "sign-in-directly",
  SIGN_IN_FUSE_MFA_CODE_SUBMISSION = "sign-in-fuse-mfa-code-submission",
  SIGN_IN_APPLY_LINK_CREATION_ACCESS_POLICIES = "sign-in-apply-link-creation-access-policies",
  SIGN_IN_MULTI_BOX_MFA = "sign-in-multi-box-mfa",
  SIGN_IN_CONCURRENT_VIEWS = "sign-in-concurrent-views",
  SIGN_IN_STRICT_MODE = "sign-in-strict-mode",
  SIGN_IN_AI_PROMPT = "sign-in-ai-prompt",
  SIGN_IN_TIME_OUT_IO_TOOLS = "sign-in-timeout-io-tools",
  SIGN_IN_NEOCLASSICALLY = "sign-in-neoclassically",
  SIGN_IN_ATTEMPT_CONTINUE = "sign-in-attempt-continue",
  NEO_FALLBACK_AGENT = "neo-fallback-agent",
  NEO_EXTRA_SUPERVISION = "neo-extra-supervision",
  WORKFLOWAI_INVOICE_EXTRACTION_TASK_VERSION = "workflowai-invoice-extraction-task-version",
  INGESTION_DATA_QUALITY_ENABLED = "ingestion-data-quality-enabled",
  AI_CRAWL_ENABLE_CLAIM_PDF_VISITS = "ai-crawl-enable-claim-pdf-visits",
  EOB_FINDINGS_V1_ENABLED = "eob-findings-v1",
  AUTO_ANALYZE_SIGN_IN_LOGS = "auto-analyze-sign-in-logs",
  REMOVE_INSURANCE_FROM_ONBOARDING = "remove-insurance-from-onboarding",
  SHOW_PROMO_CODE_INPUT = "show-promo-code-input",
  SHOW_PAYMENT_IN_SETTINGS = "show-payment-in-settings",
  AI_CRAWL_SPECIALIST_INSTRUCTIONS_REF = "ai-crawl-specialist-instructions-ref",
  USE_GENERIC_AGENT_FOR_ONBOARDING = "use-generic-agent-for-onboarding",
  MANAGED_MFA_ENABLED = "managed-mfa-enabled",
  BOT_CONTROL_ENABLED = "bot-control-enabled",
  VELLUM_BENEFIT_SUMMARY_RELEASE_TAG = "vellum-benefit-summary-release-tag",
  BENEFIT_SUMMARY_WORKFLOW_ENABLED = "benefit-summary-workflow-enabled",
  OAUTH_ENABLED = "o-auth-enabled",
  VONAGE_SMS_CC_EMAILS = "vonage-sms-cc-emails",
  PLAN_DOCUMENTS_LOADING_UI_TIMEOUT_SECONDS = "plan-documents-loading-ui-timeout-seconds",
  INSURANCE_LINKING_PROGRESS_TEXTS = "insurance-linking-progress-texts",
  SHOW_P3_ENDPOINTS = "show-p3-endpoints",
  FORCE_SHOW_WELCOME_SCREEN = "force-show-welcome-screen",
  CASE_AUTOMATION_ROUTER_WORKFLOW = "case-automation-router-workflow",
  CASE_AUTOMATION_SPECIAL_MESSAGING = "case-automation-special-messaging",
  CASE_AUTOMATION_RECORD_FACT_TOOL = "case-automation-record-fact-tool",
  CASE_AUTOMATION_OPEN_ENROLLMENT_WORKFLOW = "case-automation-open-enrollment-workflow",
  CASE_AUTOMATION_PROVIDER_SEARCH_WORKFLOW = "case-automation-provider-search-workflow",
  FORCE_CASE_AUTOMATION_PRE_AUTH_WORKFLOW = "force-case-automation-pre-auth-workflow",
  BILL_CHECKER_AI_ANALYSIS_IMPROVEMENTS = "bill-checker-ai-analysis-improvements",
  BILL_CHECKER_INVOICES_PRICE_TRANSPARENCY = "bill-checker-invoices-price-transparency",
  CASE_AUTOMATION_HIPAA_INSURER_RESOLUTION = "case-automation-hipaa-insurer-resolution",
  SHOW_POST_ONBOARDING_REFERRAL_MODAL = "show-post-onboarding-referral-modal",
  SHOW_EHR_DEFAULT_LINKS = "show-ehr-default-links",
  USE_REDUCTO_OCR = "use-reducto-ocr",
  EXPEDITE_BILL_UPLOAD = "expedite-bill-upload",
  CASE_AUTOMATION_RESOLUTION_CONFIDENCE = "case-automation-resolution-confidence",
  DOCUMENT_ANALYSIS_LOADING_CONFIG = "document-analysis-loading-config",
  NEW_CASELIST_UI = "new-caselist-ui",
}

export type Contexts =
  | "endpointSlug"
  | "user"
  | "toPhoneNumber"
  | "executionId";

/** Acceptable contexts for LaunchDarkly. */
export type KnownLDContext = { kind: "multi" } & {
  // This enforces that at least one of the Context properties is present.
  // Otherwise, LD's client will error at runtime.
  [K in Contexts]: { [P in K]: LDContextCommon } & {
    [P in Exclude<Contexts, K>]?: LDContextCommon;
  };
}[Contexts];

({}) as KnownLDContext satisfies LDContext;

export const EMPTY_LD_CONTEXT: KnownLDContext = {
  kind: "multi",
  user: { key: "empty" },
};
